import React, { useContext, useEffect, useMemo } from 'react';
import { FunctionComponent } from 'react';
import { useActiveMonth } from '../../../hooks';
import styles from './styles.module.scss';
import { DateRange, isDateInRange } from '../../../services/DateRange';
import { CalendarContext } from '../../../contexts';
import { DefaultWeekdayLabel, WeekdayLabelProps } from '../../WeekdayLabel';
import { DefaultEventComponent, EventComponentProps } from '../EventComponent';
import classNames from 'classnames';
import { useWeekdayLabels } from '../../../hooks/useWeekdayLabels';
import { useFirstDayOfWeekOffset } from '../../../hooks/useFirstDayOfWeekOffset';

export type CalendarEventsProps<T> = {
    events: Array<CalendarEvent<T>>;
    WeekdayLabel?: FunctionComponent<WeekdayLabelProps>;
    EventComponent?: FunctionComponent<EventComponentProps<T>>;
    onVisibleRangeChanged?: (range: DateRange) => void;
    className?: string;
};

export type CalendarEvent<T> = {
    range: DateRange;
    label: string;
    data: T;
};

export const EventMonthGrid = <T,>({
    events,
    WeekdayLabel = DefaultWeekdayLabel,
    EventComponent = DefaultEventComponent,
    onVisibleRangeChanged = () => {},
    className,
}: CalendarEventsProps<T>) => {
    const { timezone, locale } = useContext(CalendarContext);
    const month = useActiveMonth();
    const previousMonth = useActiveMonth(-1);

    const weekdayLabels = useWeekdayLabels({ WeekdayLabel });

    const firstDayOffset = useFirstDayOfWeekOffset(month.days[0].from);
    const daysVisibleFromPreviousMonth = useMemo(() => {
        if (firstDayOffset === 0) {
            return [];
        }
        return previousMonth.days.slice(-firstDayOffset);
    }, [firstDayOffset, previousMonth]);

    const visibleDays = useMemo(() => {
        return [...daysVisibleFromPreviousMonth, ...month.days];
    }, [daysVisibleFromPreviousMonth, month]);

    useEffect(() => {
        onVisibleRangeChanged({ from: visibleDays[0].from, to: month.range.to });
    }, [onVisibleRangeChanged, visibleDays, month]);

    const days = useMemo(() => {
        return visibleDays.map((day) => {
            const eventsOnDay = events.filter((event) => isDateInRange(event.range.from, day));
            const isToday = isDateInRange(new Date(), day);
            const hasPassed = Date.now() > day.to.getTime();
            const isPreviousMonth = month.range.from.getTime() > day.from.getTime();
            return (
                <div
                    key={day.from.toISOString()}
                    className={styles.day}
                    data-today={isToday || undefined}
                    data-previous-month={isPreviousMonth || undefined}
                    data-passed={hasPassed || undefined}
                    data-date={day.from.toISOString()}
                >
                    <span className={styles.dayLabel}>{day.from.getDate()}</span>
                    {eventsOnDay.map((event) => {
                        const doesEventSpanMultipleDays =
                            event.range.to.getTime() > day.to.getTime();
                        return (
                            <EventComponent
                                event={event}
                                spansMultipleDays={doesEventSpanMultipleDays}
                                timezone={timezone}
                                locale={locale}
                            />
                        );
                    })}
                </div>
            );
        });
    }, [visibleDays, events, month, locale, timezone, EventComponent]);

    return (
        <div className={classNames(styles.container, className)}>
            <div className={styles.calendarEvents}>
                <div>{weekdayLabels[0]}</div>
                <div>{weekdayLabels[1]}</div>
                <div>{weekdayLabels[2]}</div>
                <div>{weekdayLabels[3]}</div>
                <div>{weekdayLabels[4]}</div>
                <div>{weekdayLabels[5]}</div>
                <div>{weekdayLabels[6]}</div>
                {days}
            </div>
        </div>
    );
};
